import ContextualEvaluator from "../../evaluator/ContextualEvaluator"
import { EvaluatorContext } from "../../evaluator/Evaluator"
import EvaluateResponse from "../../EvaluateResponse"
import Evaluation from "../../Evaluation"
import RemoteEvaluateRequest from "./RemoteEvaluateRequest"
import RemoteEvaluateResult from "../../../workspace/evaluation/entity/RemoteEvaluateResult"
import { Metrics } from "../../../metrics/Metrics"
import Logger from "../../../logger"
import ObjectUtil from "../../../util/ObjectUtil"

/**
 * Base for evaluators that read a server-resolved result. Before delegating to `remoteEvaluate`,
 * it resolves the entity's references from the workspace evaluation and adds them to the context
 * so nested results are available during evaluation.
 */
export default abstract class RemoteEvaluator<
  REQ extends RemoteEvaluateRequest,
  RESP extends EvaluateResponse
> extends ContextualEvaluator<REQ, RESP> {
  protected abstract remoteEvaluate(request: REQ, context: EvaluatorContext): RESP

  protected doEvaluate(request: REQ, context: EvaluatorContext): RESP {
    this.resolveReferences(request, context)
    return this.remoteEvaluate(request, context)
  }

  private resolveReferences(request: REQ, context: EvaluatorContext): void {
    request.entity.references.forEach((reference) => {
      if (ObjectUtil.isNotNullOrUndefined(context.getEvaluation(reference))) return

      const result = request.workspace.result(reference)
      if (ObjectUtil.isNullOrUndefined(result)) {
        Metrics.counter("workspace.evaluation.reference.unresolved", {
          "service.type": reference.serviceType,
          "entity.id": String(reference.id)
        }).increment()
        Logger.log.warn(
          `Reference result not found (reference=${reference.serviceType}:${reference.id}, ` +
            `root=${request.entity.serviceType}:${request.entity.id})`
        )
        return
      }

      context.addEvaluation(this.resolveReference(request, result))
    })
  }

  /**
   * Flattens a resolved reference result into an evaluation before it enters the context.
   * Subclasses override to post-process the referenced evaluation (e.g. reason attribution).
   */
  protected resolveReference(_request: REQ, result: RemoteEvaluateResult): Evaluation {
    return result.toEvaluation()
  }
}
